import { Dispatch, SetStateAction } from "react";
import { Grid } from "@mui/material";
import { TextInput } from "../../components-redesign/TextInput/TextInput";
import LoginHeader from "./LoginHeader";
import ChangeAccount from "./ChangeAccount";
import PrimaryLink from "../../components-redesign/StyledLinks/PrimaryLink";
import PrimaryButton from "../../components-redesign/StyledButtons/PrimaryButton";
import { useLocalTranslationState } from "../../context/AppContext/AppContext";
import { styled } from "@mui/material/styles";

const FormContainer = styled("form")(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  width: '100%',
  alignSelf: 'stretch',
  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
  },
}));

const LoginBtnContainer = styled("div")(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  flexDirection: "column",
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(1),
  },
}));

const PasswordResetLinkContainer = styled("div")({
  display: "flex",
  alignSelf: "flex-start",
  justifyContent: "left",
});

const EmptyDivContainer = styled("div")({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
});

const ChangeAccountLinkContainer = styled("div")(({ theme }) => ({
  paddingBottom: theme.spacing(5),
}));

interface EnterUsernameProps {
  isLoading;
  username;
  password;
  error: boolean;
  authError: string;
  setPassword: (password) => void;
  onBack: () => void;
  loginWithUserDetails: () => void;
  setAuthError: Dispatch<SetStateAction<string>>;
  setLoginState: (state) => void;
  setError: Dispatch<SetStateAction<boolean>>;
}

const LoginEnterPassword = ({
  isLoading,
  username,
  password,
  setPassword,
  onBack,
  loginWithUserDetails,
  setAuthError,
  setLoginState,
  setError,
  authError,
}: EnterUsernameProps) => {

  const { _translate } = useLocalTranslationState();

  const isValid = () => {
    if (username && password) {
      setError(false);
      setAuthError("")
      return true
    } else {
      setError(true);
      setAuthError(_translate("Error, please complete this field"))
      return false
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isValid()) {
      loginWithUserDetails();
    }
  };

  return (
    <>
      <FormContainer noValidate onSubmit={handleSubmit}>
        <LoginHeader text={_translate("Log in")} />

        <EmptyDivContainer sx={(theme) => ({
          paddingBottom: theme.spacing(2)
        })} />

        <ChangeAccountLinkContainer>
          <ChangeAccount
            username={username}
            onClick={() => {
              setError(false)
              setAuthError("")
              onBack()
            }} />
        </ChangeAccountLinkContainer>
        <input
          type="text"
          id="username"
          name="username"
          autoComplete="username"
          value={username}
          aria-hidden="true"
          style={{ display: 'none' }}
          readOnly
        />
        <TextInput
          fieldName="password"
          id="password"
          dataTestId="password"
          label={_translate("Password")}
          type={'password'}
          value={password}
          isDisabled={isLoading}
          isRequired={true}
          errorInPlainText={false}
          error={authError}
          onClear={() => setPassword('')}
          onChange={(event) => {
            const value = event.target.value;
            setPassword(() => value);
            if (value) {
              setError(false);
              setAuthError('')
            }
          }}
        />

        <Grid
          container
          direction="column"
          sx={(theme) => ({
            paddingTop: theme.spacing(.5),
            alignItems: 'stretch',
            gap: 2,
          })}
        >
          <Grid>
            <PasswordResetLinkContainer>
              <PrimaryLink
                text={_translate("Forgotten password")}
                onClick={setLoginState}
              />
            </PasswordResetLinkContainer>
          </Grid>

          <Grid>
            <LoginBtnContainer>
              <PrimaryButton
                id="login-password-btn"
                text={_translate("Log in")}
                dataTestId="password-btn"
              />
            </LoginBtnContainer>
          </Grid>
        </Grid>
      </FormContainer>
    </>
  );
};

export default LoginEnterPassword;
